import { useMutation } from "@apollo/client";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import gql from "graphql-tag";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import FlashMessage from "../../components/FlashMessage";
import Analytics from "../../utils/analytics";
import useStyles from "./styles";
import VerifyEmailIcon from "../../assets/images/logo.png";
import { LoginWrapper } from "../Wrapper";
import { createUser, requestOtp, verifyOtp } from "../../apollo/server";
import UserContext from "../../context/User";
import OtpInput from "react-otp-input";
import { useTranslation } from 'react-i18next';
import ConfigurableValues from "../../config/constants";

const REQUEST_OTP = gql`
  ${requestOtp}
`;
const VERIFY_OTP = gql`
  ${verifyOtp}
`;
const CREATEUSER = gql`
  ${createUser}
`;
function VerifyEmail() {
  const { t } = useTranslation();
  const formRef = useRef();
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const { state: user } = useLocation();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setTokenAsync } = useContext(UserContext);
  const [otpError, setOtpError] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const [otp, setOtp] = useState("");
  // Two-step verification: verify email first, then phone, then create the
  // account. The phone step is skipped if the user supplied no phone number.
  const [step, setStep] = useState("email");
  const { SKIP_EMAIL_VERIFICATION } = ConfigurableValues()
  const handleBackNavigation = () => {
    // Use history.push to navigate to the desired route
    navigate("/registration");
  };

  useEffect(() => {
    // Add an event listener for the popstate event
    window.addEventListener("popstate", handleBackNavigation);

    // Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
    };
  });
  const [createUser, { loading: createLoading }] = useMutation(CREATEUSER, {
    onCompleted: onCompletedCreate,
    onError: onErrorCreate,
  });
  const [sendOtp] = useMutation(REQUEST_OTP, {
    onCompleted: onCompletedOtp,
    onError: onErrorOtp,
  });
  const [verifyOtpMutation, { loading: verifyLoading }] = useMutation(VERIFY_OTP);

  function onErrorOtp(e) {
    setLoading(false);
    setError(e.message);
  }

  function onCompletedOtp(e) {
    // server generated + sent the code
  }

  function onErrorCreate(e) {
    setLoading(false);
    setError(e.message);
  }
  async function onCompletedCreate({ createUser }) {
    try {
      await Analytics.identify(
        {
          userId: createUser.userId,
          name: createUser.name,
          email: createUser.email,
        },
        createUser.userId
      );
      await Analytics.track(Analytics.events.USER_CREATED_ACCOUNT, {
        userId: createUser.userId,
        name: createUser.name,
        email: createUser.email,
      });
      navigate("/", {
        replace: true,
      });
      setTokenAsync(createUser.token);
    } catch (e) {
      setError("Something went wrong");
      console.log(e);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (!user?.email) return;
    sendOtp({
      variables: { identifier: user.email, channel: "EMAIL", purpose: "SIGNUP" },
    });
    setSeconds(30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  useEffect(() => {
    const myInterval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        clearInterval(myInterval);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  });

  const toggleSnackbar = useCallback(() => {
    setError("");
  }, []);

  const doCreateUser = useCallback(() => {
    createUser({
      variables: {
        phone: user.phone,
        email: user.email,
        password: user.password,
        name: user.name,
        picture: "",
      },
    });
  }, [createUser, user.email, user.name, user.password, user.phone]);

  // Move from the email step to the phone step: reset the OTP box, send the
  // SMS code and restart the resend cooldown.
  const goToPhoneStep = useCallback(() => {
    setStep("phone");
    setOtp("");
    setOtpError(false);
    setError("");
    sendOtp({ variables: { identifier: user.phone, channel: "SMS", purpose: "PHONE" } });
    setSeconds(30);
  }, [sendOtp, user.phone]);

  const onCodeFilled = useCallback(async (code) => {
    if (SKIP_EMAIL_VERIFICATION) {
      doCreateUser();
      return;
    }
    setOtpError(false);
    setError("");
    if (step === "email") {
      try {
        // Server validates the code; never trust the client.
        await verifyOtpMutation({
          variables: { identifier: user.email, channel: "EMAIL", purpose: "SIGNUP", code },
        });
        // Email verified — verify the phone next if one was provided,
        // otherwise create the account straight away.
        if (user.phone) {
          goToPhoneStep();
        } else {
          doCreateUser();
        }
      } catch (e) {
        setOtpError(true);
        setError(e.message || "Incorrect code");
      }
      return;
    }
    // step === "phone"
    try {
      await verifyOtpMutation({
        variables: { identifier: user.phone, channel: "SMS", purpose: "PHONE", code },
      });
      doCreateUser();
    } catch (e) {
      setOtpError(true);
      setError(e.message || "Incorrect code");
    }
  }, [SKIP_EMAIL_VERIFICATION, step, doCreateUser, goToPhoneStep, verifyOtpMutation, user.email, user.phone]);

  const resendOtp = () => {
    setError("");
    setOtpError(false);
    if (step === "phone") {
      sendOtp({ variables: { identifier: user.phone, channel: "SMS", purpose: "PHONE" } });
    } else {
      sendOtp({ variables: { identifier: user.email, channel: "EMAIL", purpose: "SIGNUP" } });
    }
    setSeconds(30);
  };
  const handleCreateUser = useCallback((val) => {
    setOtp(val);
    if (val.length === 6) {
      onCodeFilled(val);
    }
  },[onCodeFilled]);

  useEffect(()=>{
    let timer = null
    if(!SKIP_EMAIL_VERIFICATION) return
    timer = setTimeout(()=>{
      doCreateUser()
    },1500)
    return ()=>{timer && clearTimeout(timer)}
  },[SKIP_EMAIL_VERIFICATION, doCreateUser])

  return user?.email ? (
    <LoginWrapper>
      <FlashMessage
        open={Boolean(error)}
        severity={"error"}
        alertMessage={error}
        handleClose={toggleSnackbar}
      />

      <Box display="flex">
        <Box m="auto" sx={{ width: 90, height: 64, overflow: "hidden" }}>
          <img
            alt="RushMyFood"
            src={VerifyEmailIcon}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top",
              transform: "scale(1.55)",
              transformOrigin: "top center",
            }}
          />
        </Box>
      </Box>
      {createLoading ? (
        <CircularProgress color="primary" />
      ) : (
        <form ref={formRef}>
          <Box mt={theme.spacing(2)} />
          <Typography variant="h5" className={classes.font700}>
            {step === "phone" ? t('verifyPhone') : t('verifyEmail')}
          </Typography>
          <Box mt={theme.spacing(2)} />
          <Typography
            variant="caption"
            className={`${classes.caption} ${classes.fontGrey}`}
          >
            {step === "phone" ? t('enterOtpPhone') : t('enterOtp')}
          </Typography>
          <Box mt={theme.spacing(2)} />
          <OtpInput
            value={otp}
            onChange={handleCreateUser}
            numInputs={6}
            containerStyle={{
              width: "100%",
              display: "flex",
              alignSelf: "center",
              backgroundColor: theme.palette.common.white,
            }}
            inputStyle={{
              width: 45,
              height: 45,
              margin: 5,
              borderRadius: 5,
              fontSize: theme.typography.h2,
              border: `1px solid ${theme.palette.grey[400]}`,
              boxShadow: theme.shadows[3],
            }}
            focusStyle={{
              outlineColor: theme.palette.grey[900],
            }}
            editable
            renderInput={(props) => <input {...props} />}
          />
          <Box mt={2} />
          {otpError && (
            <Typography variant={"h6"} style={{ color: "red", fontSize: 14 }}>
              {t('invalidCode')}
            </Typography>
          )}
          <Box mt={theme.spacing(8)} />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            type="email"
            disableElevation
            className={classes.btnBase}
            disabled={seconds !== 0}
            onClick={(e) => {
              e.preventDefault();
              resendOtp();
            }}
          >
            {loading ? (
              <CircularProgress color="primary" />
            ) : (
              <Typography
                variant="caption"
                className={`${classes.caption} ${classes.font700}`}
              >
                {t('resendCode')}
              </Typography>
            )}
          </Button>
          <Box mt={theme.spacing(2)} />
          <Typography variant="caption" className={`${classes.caption}`}>
            {seconds === 0 ? "" : `${t('retryAfter')} ${seconds}s`}
          </Typography>
        </form>
      )}
    </LoginWrapper>
  ) : (
    <Navigate to="/login" />
  );
}

export default VerifyEmail;
